/**
 * 쿠키 관리 서비스
 * 
 * 브라우저 쿠키를 쉽게 설정, 조회, 삭제할 수 있는 유틸리티 함수들을 제공합니다.
 */

/**
 * 쿠키 설정
 * 
 * @param {string} name 쿠키 이름
 * @param {string} value 쿠키 값
 * @param {number} days 유효 기간(일). 기본값 30일
 */
export const setCookie = (name, value, days = 30) => {
  try {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + days);
    
    const cookieValue = encodeURIComponent(value) + 
      ((days === null) ? '' : `; expires=${expirationDate.toUTCString()}`);
      
    // 쿠키 설정 전 로깅
    console.log(`쿠키 설정: ${name}=${value} (${days}일)`);
    
    document.cookie = `${name}=${cookieValue}; path=/; SameSite=Lax`;
    
    // 쿠키 설정 후 모든 쿠키 로깅
    console.log('현재 모든 쿠키:', document.cookie);
  } catch (error) {
    console.error('쿠키 설정 오류:', error);
  }
};

/**
 * 쿠키 조회
 * 
 * @param {string} name 조회할 쿠키 이름
 * @returns {string|null} 쿠키 값 또는 null(존재하지 않는 경우)
 */
export const getCookie = (name) => {
  try {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    
    return null;
  } catch (error) {
    console.error('쿠키 조회 오류:', error);
    return null;
  }
};

/**
 * 쿠키 삭제
 * 
 * @param {string} name 삭제할 쿠키 이름
 */
export const deleteCookie = (name) => {
  try {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
  } catch (error) {
    console.error('쿠키 삭제 오류:', error);
  }
};

/**
 * 모든 쿠키 조회
 * 
 * @returns {Object} 모든 쿠키의 이름-값 쌍을 포함하는 객체
 */
export const getAllCookies = () => {
  try {
    const cookies = {};
    const cookiesList = document.cookie.split(';');
    
    for (let i = 0; i < cookiesList.length; i++) {
      const cookie = cookiesList[i].trim();
      const separatorIndex = cookie.indexOf('=');
      
      if (separatorIndex > 0) {
        const name = cookie.substring(0, separatorIndex);
        const value = decodeURIComponent(cookie.substring(separatorIndex + 1));
        cookies[name] = value;
      }
    }
    
    return cookies;
  } catch (error) {
    console.error('모든 쿠키 조회 오류:', error);
    return {};
  }
};

/**
 * 사용자 정보를 쿠키에서 가져오기
 * JWT 토큰을 디코딩하여 사용자 정보 반환
 * 
 * @returns {Object|null} 사용자 정보 객체 또는 null
 */
export const getUserFromCookie = () => {
  try {
    // JWT 토큰이나 사용자 정보가 들어있는 쿠키들을 확인
    const userInfo = getCookie('user_info');
    if (userInfo) {
      return JSON.parse(userInfo);
    }

    // JWT 토큰에서 사용자 정보 추출 시도 - 실제 쿠키 이름들 확인
    const token = getCookie('access_token') || getCookie('jwt_token') || getCookie('token');
    console.log('CookieService - 토큰 찾기:', {
      access_token: getCookie('access_token'),
      jwt_token: getCookie('jwt_token'),
      token: getCookie('token'),
      selectedToken: token
    }); // 디버깅용 로그
    
    if (token) {
      // JWT 토큰 디코딩 (단순한 Base64 디코딩)
      const tokenParts = token.split('.');
      console.log('CookieService - JWT 토큰 파트:', tokenParts.length); // 디버깅용 로그
      
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('CookieService - JWT 페이로드:', payload); // 디버깅용 로그
        
        return {
          id: payload.user_id,
          username: payload.username,
          is_staff: payload.is_staff || false,
          is_superuser: payload.is_superuser || false,
          email: payload.email || ''
        };
      }
    }

    console.log('CookieService - 사용자 정보를 찾을 수 없음'); // 디버깅용 로그
    return null;
  } catch (error) {
    console.error('사용자 쿠키 정보 조회 오류:', error);
    return null;
  }
};

/**
 * 모든 쿠키 삭제 (로그아웃 시 사용)
 */
export const clearCookies = () => {
  try {
    const cookies = getAllCookies();
    Object.keys(cookies).forEach(cookieName => {
      deleteCookie(cookieName);
    });
    
    // 주요 인증 관련 쿠키들을 명시적으로 삭제
    const authCookies = ['jwt_token', 'token', 'user_info', 'current_tenant'];
    authCookies.forEach(cookieName => {
      deleteCookie(cookieName);
    });
    
    console.log('모든 쿠키가 삭제되었습니다.');
  } catch (error) {
    console.error('쿠키 전체 삭제 오류:', error);
  }
};

export default {
  set: setCookie,
  get: getCookie,
  delete: deleteCookie,
  getAll: getAllCookies,
  getUserFromCookie,
  clearCookies
}; 